import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "sahyog_admin_token";
const PARTNER_TOKEN_KEY = "partner_token";
const INVESTOR_TOKEN_KEY = "investor_token";

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const partnerTokenStorage = {
  get: () => localStorage.getItem(PARTNER_TOKEN_KEY),
  set: (t) => localStorage.setItem(PARTNER_TOKEN_KEY, t),
  clear: () => localStorage.removeItem(PARTNER_TOKEN_KEY),
};

export const investorTokenStorage = {
  get: () => localStorage.getItem(INVESTOR_TOKEN_KEY),
  set: (t) => localStorage.setItem(INVESTOR_TOKEN_KEY, t),
  clear: () => localStorage.removeItem(INVESTOR_TOKEN_KEY),
};

// ---------- Public axios (no auth) ----------
export const publicApi = axios.create({ baseURL: API });

// ---------- Admin axios (Bearer auth, auto-clears on 401) ----------
export const adminApi = axios.create({ baseURL: API, withCredentials: true });

adminApi.interceptors.request.use((config) => {
  const t = tokenStorage.get();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === 401) {
      tokenStorage.clear();
    }
    return Promise.reject(err);
  }
);

// ---------- Partner axios (Bearer auth) ----------
export const partnerApi = axios.create({ baseURL: API });

partnerApi.interceptors.request.use((config) => {
  const t = partnerTokenStorage.get();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

partnerApi.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === 401) {
      partnerTokenStorage.clear();
    }
    return Promise.reject(err);
  }
);

// ---------- Investor axios (Bearer auth) ----------
export const investorApi = axios.create({ baseURL: API });

investorApi.interceptors.request.use((config) => {
  const t = investorTokenStorage.get();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

investorApi.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === 401) {
      investorTokenStorage.clear();
    }
    return Promise.reject(err);
  }
);

// =========================================================================
// Service helpers — one function per public-facing API call.
// Pages should import these instead of touching axios directly.
// =========================================================================

// ----- Leads (public capture + admin) -----
export const Leads = {
  create: (payload) => publicApi.post("/leads", payload).then((r) => r.data),
  list: (params = {}) => adminApi.get("/leads", { params }).then((r) => r.data),
  stats: () => adminApi.get("/leads/stats").then((r) => r.data),
  updateStatus: (id, status) =>
    adminApi.patch(`/leads/${id}`, { status }).then((r) => r.data),
  exportCsvUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/leads/export.csv${qs ? `?${qs}` : ""}`;
  },
};

// ----- Applications -----
export const Applications = {
  create: (payload) => publicApi.post("/applications", payload).then((r) => r.data),
  list: (params = {}) => adminApi.get("/applications", { params }).then((r) => r.data),
  updatePayment: (id, payment_status) =>
    adminApi
      .patch(`/applications/${id}/payment`, { payment_status })
      .then((r) => r.data),
};

// ----- Grievances -----
export const Grievances = {
  create: (payload) => publicApi.post("/grievances", payload).then((r) => r.data),
  list: (params = {}) => adminApi.get("/grievances", { params }).then((r) => r.data),
  update: (id, payload) =>
    adminApi.patch(`/grievances/${id}`, payload).then((r) => r.data),
};

// ----- Offers (public list + admin CRUD) -----
export const Offers = {
  list: (only_active = true) =>
    publicApi.get("/offers", { params: { only_active } }).then((r) => r.data),
  create: (payload) => adminApi.post("/offers", payload).then((r) => r.data),
  update: (id, payload) => adminApi.patch(`/offers/${id}`, payload).then((r) => r.data),
  remove: (id) => adminApi.delete(`/offers/${id}`).then((r) => r.data),
};

// ----- Blogs (public list/get + admin CRUD) -----
export const Blogs = {
  list: (only_published = true) =>
    publicApi.get("/blogs", { params: { only_published } }).then((r) => r.data),
  get: (slug) => publicApi.get(`/blogs/${slug}`).then((r) => r.data),
  create: (payload) => adminApi.post("/blogs", payload).then((r) => r.data),
  update: (id, payload) => adminApi.patch(`/blogs/${id}`, payload).then((r) => r.data),
  remove: (id) => adminApi.delete(`/blogs/${id}`).then((r) => r.data),
};

// ----- Auth (admin) -----
export const Auth = {
  login: (email, password) =>
    publicApi.post("/auth/login", { email, password }).then((r) => r.data),
  login2fa: (payload) =>
    publicApi
      .post("/auth/login/2fa", payload, { withCredentials: true })
      .then((r) => r.data),
  logout: () => adminApi.post("/auth/logout").then((r) => r.data),
  me: () => adminApi.get("/auth/me").then((r) => r.data),
  forgotPassword: (payload) =>
    publicApi.post("/auth/forgot-password", payload).then((r) => r.data),
  resetPassword: (payload) =>
    publicApi.post("/auth/reset-password", payload).then((r) => r.data),
  changePassword: (payload) =>
    adminApi.post("/auth/change-password", payload).then((r) => r.data),
};

// ----- Admin 2FA (TOTP) -----
export const TwoFactor = {
  status: () => adminApi.get("/auth/2fa/status").then((r) => r.data),
  setup: () => adminApi.post("/auth/2fa/setup").then((r) => r.data),
  verifySetup: (code) =>
    adminApi.post("/auth/2fa/verify-setup", { code }).then((r) => r.data),
  disable: (current_password, code) =>
    adminApi
      .post("/auth/2fa/disable", { current_password, code })
      .then((r) => r.data),
  regenerateRecovery: (code) =>
    adminApi
      .post("/auth/2fa/regenerate-recovery", { code })
      .then((r) => r.data),
  revokeDevices: () =>
    adminApi.post("/auth/2fa/revoke-devices").then((r) => r.data),
};

// ----- Admin: Staff management -----
export const AdminStaff = {
  list: (params = {}) =>
    adminApi.get("/admin/staff", { params }).then((r) => r.data),
  create: (payload) =>
    adminApi.post("/admin/staff", payload).then((r) => r.data),
  update: (id, payload) =>
    adminApi.patch(`/admin/staff/${id}`, payload).then((r) => r.data),
  enable: (id) =>
    adminApi.post(`/admin/staff/${id}/enable`).then((r) => r.data),
  disable: (id) =>
    adminApi.post(`/admin/staff/${id}/disable`).then((r) => r.data),
  resetPassword: (id) =>
    adminApi.post(`/admin/staff/${id}/reset-password`).then((r) => r.data),
};

// ----- Admin: Audit log -----
export const AdminAudit = {
  list: (params = {}) =>
    adminApi.get("/admin/audit-log", { params }).then((r) => r.data),
};

// ----- Admin: Lead verification + delete -----
export const AdminLeadOps = {
  verify: (leadId, status, notes) =>
    adminApi.post(`/leads/${leadId}/verify`, { status, notes }).then((r) => r.data),
  assign: (leadId, assignedTo) =>
    adminApi.post(`/leads/${leadId}/assign`, { assigned_to: assignedTo }).then((r) => r.data),
  softDelete: (leadId) =>
    adminApi.post(`/leads/${leadId}/soft-delete`).then((r) => r.data),
  restore: (leadId) =>
    adminApi.post(`/leads/${leadId}/restore`).then((r) => r.data),
  permanentDelete: (leadId, confirmation) =>
    adminApi.delete(`/leads/${leadId}`, { data: { confirmation } }).then((r) => r.data),
  audit: (leadId) =>
    adminApi.get(`/leads/${leadId}/audit`).then((r) => r.data),
  listTrash: (params = {}) =>
    adminApi.get("/leads", { params: { ...params, trash: true } }).then((r) => r.data),
};

// ----- CRM: activity timeline + follow-ups + bulk + pipeline + dashboard -----
export const CRM = {
  timeline: (leadId) => adminApi.get(`/leads/${leadId}/timeline`).then((r) => r.data),
  addActivity: (leadId, kind, body) =>
    adminApi.post(`/leads/${leadId}/activity`, { kind, body }).then((r) => r.data),
  createFollowUp: (leadId, payload) =>
    adminApi.post(`/leads/${leadId}/followups`, payload).then((r) => r.data),
  updateFollowUp: (leadId, followupId, payload) =>
    adminApi.patch(`/leads/${leadId}/followups/${followupId}`, payload).then((r) => r.data),
  listFollowUps: (params = {}) =>
    adminApi.get("/followups", { params }).then((r) => r.data),
  bulk: (payload) => adminApi.post("/leads/bulk", payload).then((r) => r.data),
  pipeline: (params = {}) =>
    adminApi.get("/admin/crm/pipeline", { params }).then((r) => r.data),
  dashboard: () => adminApi.get("/admin/crm/dashboard").then((r) => r.data),
  dashboardPdfUrl: () => `${API}/api/admin/crm/dashboard.pdf`,
};

// ----- Customer 360 -----
export const Customer360 = {
  search: (q) => adminApi.get("/admin/customer-360/search", { params: { q } }).then((r) => r.data),
  fetch: (phone) => adminApi.get(`/admin/customer-360/${encodeURIComponent(phone)}`).then((r) => r.data),
  merge: (source_phone, target_phone, reason) =>
    adminApi.post("/admin/customer-360/merge", { source_phone, target_phone, reason }).then((r) => r.data),
};

// ----- Saved Views (per-user lead-table presets) -----
export const SavedViews = {
  list: () => adminApi.get("/admin/saved-views").then((r) => r.data),
  create: (name, filters) => adminApi.post("/admin/saved-views", { name, filters }).then((r) => r.data),
  remove: (id) => adminApi.delete(`/admin/saved-views/${id}`).then((r) => r.data),
};

// ----- Utility: decode the admin JWT (client-side, no verification). -----
// Used to gate UI elements by role. Backend is the source of truth for
// permissions — this is purely for showing/hiding buttons.
export function currentUser() {
  const token = tokenStorage.get();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      id: payload.sub,
      email: payload.email,
      role: payload.role || "operator",
    };
  } catch {
    return null;
  }
}

// ----- Payments -----
export const Payments = {
  createCheckoutSession: (application_id, origin_url) =>
    publicApi
      .post("/payments/checkout/session", { application_id, origin_url })
      .then((r) => r.data),
  getCheckoutStatus: (session_id) =>
    publicApi.get(`/payments/checkout/status/${session_id}`).then((r) => r.data),
  receiptUrl: (application_id) => `${API}/payments/receipt/${application_id}`,
};

// ----- Uploads -----
export const Uploads = {
  applicationDoc: (file, { kind = "other", application_id, role, phone } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    if (role) fd.append("role", role);
    if (phone) fd.append("phone", phone);
    if (application_id) fd.append("application_id", application_id);
    return publicApi
      .post("/uploads/application-doc", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  listForApplication: (application_id) =>
    adminApi
      .get(`/uploads/by-application/${application_id}`)
      .then((r) => r.data),
  // Build a direct URL for downloading/viewing the file (auth via cookie if private)
  fileUrl: (file_id) => `${API}/uploads/application-doc/${file_id}`,
};

// ----- Partner Portal -----
export const Partner = {
  register: (payload) =>
    publicApi.post("/partner/auth/register", payload).then((r) => r.data),
  login: (payload) =>
    publicApi.post("/partner/auth/login", payload).then((r) => r.data),
  forgotPassword: (payload) =>
    publicApi.post("/partner/auth/forgot-password", payload).then((r) => r.data),
  resetPassword: (payload) =>
    publicApi.post("/partner/auth/reset-password", payload).then((r) => r.data),
  me: () => partnerApi.get("/partner/me").then((r) => r.data),
  stats: () => partnerApi.get("/partner/stats").then((r) => r.data),
  leads: (params = {}) =>
    partnerApi.get("/partner/leads", { params }).then((r) => r.data),
  createLead: (payload) =>
    partnerApi.post("/partner/leads", payload).then((r) => r.data),
  updateLead: (id, payload) =>
    partnerApi.patch(`/partner/leads/${id}`, payload).then((r) => r.data),
  team: () => partnerApi.get("/partner/team").then((r) => r.data),
};

// ----- Investor Portal -----
export const Investor = {
  login: (payload) =>
    publicApi.post("/investor/auth/login", payload).then((r) => r.data),
  forgotPassword: (payload) =>
    publicApi.post("/investor/auth/forgot-password", payload).then((r) => r.data),
  resetPassword: (payload) =>
    publicApi.post("/investor/auth/reset-password", payload).then((r) => r.data),
  me: () => investorApi.get("/investor/me").then((r) => r.data),
  stats: () => investorApi.get("/investor/stats").then((r) => r.data),
  investments: () => investorApi.get("/investor/investments").then((r) => r.data),
  initiate: (payload) =>
    investorApi.post("/investor/investments/initiate", payload).then((r) => r.data),
  agreementUrl: (investment_id) => `${API}/investor/agreement/${investment_id}`,
};

// ----- Admin Investors -----
export const AdminInvestors = {
  list: () => adminApi.get("/admin/investors").then((r) => r.data),
  get: (id) => adminApi.get(`/admin/investors/${id}`).then((r) => r.data),
  create: (payload) => adminApi.post("/admin/investors", payload).then((r) => r.data),
  addInvestment: (id, payload) =>
    adminApi.post(`/admin/investors/${id}/investments`, payload).then((r) => r.data),
  resendActivation: (id) =>
    adminApi.post(`/admin/investors/${id}/resend-activation`).then((r) => r.data),
  remove: (id) => adminApi.delete(`/admin/investors/${id}`).then((r) => r.data),
};

// ----- Eligibility Check (public, ₹1,500 rule-based) -----
export const Eligibility = {
  apply: (formFields, files, fileKinds) => {
    const fd = new FormData();
    Object.entries(formFields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    (files || []).forEach((f, i) => {
      fd.append("files", f);
      fd.append("file_kinds", (fileKinds && fileKinds[i]) || "other");
    });
    return publicApi
      .post("/eligibility/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  checkout: (checkId, originUrl) =>
    publicApi
      .post(`/eligibility/checkout/${checkId}`, { origin_url: originUrl })
      .then((r) => r.data),
  status: (sessionId) =>
    publicApi.get(`/eligibility/status/${sessionId}`).then((r) => r.data),
  result: (checkId) =>
    publicApi.get(`/eligibility/result/${checkId}`).then((r) => r.data),
};

// ----- Admin Eligibility Checks -----
export const AdminEligibility = {
  list: (params = {}) =>
    adminApi.get("/admin/eligibility-checks", { params }).then((r) => r.data),
  get: (checkId) =>
    adminApi.get(`/admin/eligibility-checks/${checkId}`).then((r) => r.data),
  aiAnalyze: (checkId) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/ai-analyze`).then((r) => r.data),
  generateLetterConfirmed: async (checkId, reason) => {
    const { job_id } = (await adminApi.post(`/admin/eligibility-checks/${checkId}/generate-letter-async`, { reason, confirm_reviewed_values: true })).data;
    for (let i = 0; i < 80; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 3000));
      // eslint-disable-next-line no-await-in-loop
      const job = (await adminApi.get(`/admin/eligibility-checks/letter-jobs/${job_id}`)).data;
      if (job.status === "done") return { ai_report: job.result, download_path: job.download_path };
      if (job.status === "failed") { const err = new Error(job.error?.message_en || "Letter generation failed"); err.response = { status: job.http_status || 500, data: { detail: job.error } }; throw err; }
    }
    throw new Error("Letter generation is taking longer than expected — check Version History in a minute.");
  },
  generateLetter: async (checkId, reason, opts = {}) => {
    // ASYNC: 202 + job_id → poll → result (avoids proxy/Cloudflare timeouts on long AI renders)
    const start = async (body) => (await adminApi.post(`/admin/eligibility-checks/${checkId}/generate-letter-async`, body)).data;
    const poll = async (jobId) => {
      for (let i = 0; i < 80; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 3000));
        // eslint-disable-next-line no-await-in-loop
        const job = (await adminApi.get(`/admin/eligibility-checks/letter-jobs/${jobId}`)).data;
        opts.onProgress?.(job);
        if (job.status === "done") return { ai_report: job.result, download_path: job.download_path, job };
        if (job.status === "failed") {
          const err = new Error(job.error?.message_en || "Letter generation failed");
          err.response = { status: job.http_status || 500, data: { detail: job.error } };
          throw err;
        }
      }
      throw new Error("Letter generation is taking longer than expected — check Version History in a minute.");
    };
    const body = reason ? { reason } : {};
    let res = await poll((await start(body)).job_id);
    return res;
  },
  review: (checkId) => adminApi.get(`/admin/eligibility-checks/${checkId}/review`).then((r) => r.data),
  saveOverride: (checkId, body) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/overrides`, body).then((r) => r.data),
  assessmentPreview: (checkId) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/assessment-preview`).then((r) => r.data),
  letterVersions: (checkId) =>
    adminApi.get(`/admin/eligibility-checks/${checkId}/letter-versions`).then((r) => r.data),
  letterVersionBlobUrl: async (checkId, versionId) => {
    const r = await adminApi.get(
      `/admin/eligibility-checks/${checkId}/letter-versions/${versionId}/pdf`,
      { responseType: "blob" },
    );
    return URL.createObjectURL(r.data);
  },
  sendLetter: async (checkId, opts = {}) => {
    const body = { ...(opts.version_id ? { version_id: opts.version_id } : {}) };
    try {
      return (await adminApi.post(`/admin/eligibility-checks/${checkId}/send-letter`, body)).data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "SUPERSEDE_CONFIRMATION_REQUIRED") {
        const ok = window.confirm(
          `PREVIOUSLY SENT VERSION: v${d.previously_sent?.version_no} · ${d.previously_sent?.reference_no} (${new Date(d.previously_sent?.sent_at).toLocaleString()})\n` +
          `VERSION ABOUT TO BE SENT: v${d.about_to_send?.version_no} · ${d.about_to_send?.reference_no}\n\n` +
          "Review the Version Compare panel first. Send the newer version now?",
        );
        if (!ok) throw e;
        return (await adminApi.post(`/admin/eligibility-checks/${checkId}/send-letter`, { ...body, confirm_supersede: true })).data;
      }
      throw e;
    }
  },
  sendTestToMe: (checkId, versionId) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/send-test`, { version_id: versionId }).then((r) => r.data),
  correctEmail: (checkId, body) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/correct-email`, body).then((r) => r.data),
  deliveryAlerts: () => adminApi.get(`/admin/eligibility-checks/delivery-alerts/mine`).then((r) => r.data),
  reviewAlert: (id) => adminApi.get(`/admin/eligibility-checks/delivery-alerts/${id}/review`).then((r) => r.data),
  resolveAlert: (id, body) => adminApi.post(`/admin/eligibility-checks/delivery-alerts/${id}/resolve`, body).then((r) => r.data),
  letterSends: (checkId) => adminApi.get(`/admin/eligibility-checks/${checkId}/letter-sends`).then((r) => r.data),
  letterCompare: (checkId, a, b) =>
    adminApi.get(`/admin/eligibility-checks/${checkId}/letter-compare`, { params: { a, b } }).then((r) => r.data),
  approveFallbackLetter: (checkId, note = "") =>
    adminApi
      .post(`/admin/eligibility-checks/${checkId}/approve-fallback-letter`, { note })
      .then((r) => r.data),
  rejectFallbackLetter: (checkId, note = "") =>
    adminApi
      .post(`/admin/eligibility-checks/${checkId}/reject-fallback-letter`, { note })
      .then((r) => r.data),
  aiPdfUrl: (checkId) => `${API}/admin/eligibility-checks/${checkId}/ai-pdf`,
  documentUrl: (checkId, docId) =>
    `${API}/admin/eligibility-checks/${checkId}/documents/${docId}`,
  // Get PDF as blob (works with axios Bearer auth — needed for iframe preview)
  aiPdfBlobUrl: async (checkId) => {
    const r = await adminApi.get(`/admin/eligibility-checks/${checkId}/ai-pdf`, {
      responseType: "blob",
    });
    return URL.createObjectURL(r.data);
  },
  documentBlobUrl: async (checkId, docId) => {
    const r = await adminApi.get(
      `/admin/eligibility-checks/${checkId}/documents/${docId}`,
      { responseType: "blob" },
    );
    return URL.createObjectURL(r.data);
  },
  docPackGenerate: (checkId) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/document-pack/generate`).then((r) => r.data),
  docPackVersions: (checkId) =>
    adminApi.get(`/admin/eligibility-checks/${checkId}/document-pack/versions`).then((r) => r.data),
  docPackLink: (checkId, versionId) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/document-pack/versions/${versionId}/link`).then((r) => r.data),
  softDelete: (checkId) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/soft-delete`).then((r) => r.data),
  restore: (checkId) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/restore`).then((r) => r.data),
  permanentDelete: (checkId, confirmation) =>
    adminApi
      .delete(`/admin/eligibility-checks/${checkId}`, { data: { confirmation } })
      .then((r) => r.data),
  listIssuedLetters: (params = {}) =>
    adminApi.get("/admin/eligibility-checks/letters/issued", { params }).then((r) => r.data),
};

// ── V2 Preview (Phase 2a · read-only) ────────────────────────────
export const Otp = {
  config: () => publicApi.get("/otp/config").then((r) => r.data),
  request: (body) => publicApi.post("/otp/request", body).then((r) => r.data),
  verify: (body) => publicApi.post("/otp/verify", body).then((r) => r.data),
};

export const EmailOps = {
  quota: () => adminApi.get("/admin/email-quota").then((r) => r.data),
  digestStatus: () => adminApi.get("/admin/delivery-alerts/digest/status").then((r) => r.data),
  digestSendNow: () => adminApi.post("/admin/delivery-alerts/digest/send-now").then((r) => r.data),
};

const CUSTOMER_SESSION_KEY = "sf_customer_session";
export const customerSession = {
  get: () => sessionStorage.getItem(CUSTOMER_SESSION_KEY),
  set: (t) => sessionStorage.setItem(CUSTOMER_SESSION_KEY, t),
  clear: () => sessionStorage.removeItem(CUSTOMER_SESSION_KEY),
};
const customerHeaders = () => ({ headers: { Authorization: `Bearer ${customerSession.get() || ""}` } });
export const CustomerLetters = {
  requestOtp: (mobile) => publicApi.post("/customer/otp/request", { mobile }).then((r) => r.data),
  verifyOtp: (mobile, otp) => publicApi.post("/customer/otp/verify", { mobile, otp }).then((r) => r.data),
  list: () => publicApi.get("/customer/letters", customerHeaders()).then((r) => r.data),
  link: (checkId) => publicApi.post(`/customer/letters/${checkId}/link`, {}, customerHeaders()).then((r) => r.data),
};

export const AdminCreditDecision = {
  submit: (checkId, body) => adminApi.post(`/admin/eligibility-checks/${checkId}/credit-decision`, body).then((r) => r.data),
};

export const AdminBureau = {
  panel: (checkId) => adminApi.get(`/admin/eligibility-checks/${checkId}/bureau`).then((r) => r.data),
  recordPan: (checkId, pan, kyc_source) => adminApi.put(`/admin/eligibility-checks/${checkId}/bureau/kyc-pan`, { pan, kyc_source }).then((r) => r.data),
  requestConsent: (checkId) => adminApi.post(`/admin/eligibility-checks/${checkId}/bureau/consent/request`).then((r) => r.data),
  recordConsent: (checkId, body) => adminApi.post(`/admin/eligibility-checks/${checkId}/bureau/consent/record`, body).then((r) => r.data),
  revokeConsent: (checkId, reason) => adminApi.post(`/admin/eligibility-checks/${checkId}/bureau/consent/revoke`, { reason }).then((r) => r.data),
  fetch: (checkId, force = false) => adminApi.post(`/admin/eligibility-checks/${checkId}/bureau/fetch`, { force_repull: force }).then((r) => r.data),
  manualUpload: (checkId, formData) =>
    adminApi.post(`/admin/eligibility-checks/${checkId}/bureau/manual-upload`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  report: (checkId, reportId) => adminApi.get(`/admin/eligibility-checks/${checkId}/bureau/reports/${reportId}`).then((r) => r.data),
  rawLink: (checkId, reportId) => adminApi.post(`/admin/eligibility-checks/${checkId}/bureau/reports/${reportId}/raw-link`).then((r) => r.data),
  integration: () => adminApi.get("/admin/integrations/credit-bureau").then((r) => r.data),
  sanctionedTest: (body) => adminApi.post("/admin/integrations/credit-bureau/sanctioned-test", body).then((r) => r.data),
};

export const PublicBureauConsent = {
  view: (token) => publicApi.get(`/bureau-consent/${token}`).then((r) => r.data),
  grant: (token, body) => publicApi.post(`/bureau-consent/${token}/grant`, body).then((r) => r.data),
};

export const AdminLetterQA = {
  status: () => adminApi.get("/admin/letter-qa/status").then((r) => r.data),
  candidates: () => adminApi.get("/admin/letter-qa/candidates").then((r) => r.data),
  sampleBlobUrl: async () => {
    const r = await adminApi.get("/admin/letter-qa/sample.pdf", { responseType: "blob" });
    return URL.createObjectURL(r.data);
  },
  testRenderBlobUrl: async (checkId) => {
    const r = await adminApi.get("/admin/letter-qa/test-render.pdf", {
      params: checkId ? { check_id: checkId } : {},
      responseType: "blob",
    });
    return URL.createObjectURL(r.data);
  },
  saveChecklist: (items) => adminApi.put("/admin/letter-qa/checklist", { items }).then((r) => r.data),
  samplePage: async (pageNo) => {
    const r = await adminApi.get(`/admin/letter-qa/sample-page/${pageNo}.png`, { responseType: "blob" });
    return { url: URL.createObjectURL(r.data), pages: Number(r.headers["x-page-count"] || 6) };
  },
  testRenderPage: async (pageNo, checkId) => {
    const r = await adminApi.get(`/admin/letter-qa/test-render-page/${pageNo}.png`, {
      params: checkId ? { check_id: checkId } : {},
      responseType: "blob",
    });
    return { url: URL.createObjectURL(r.data), pages: Number(r.headers["x-page-count"] || 6) };
  },
  approve: (reason) => adminApi.post("/admin/letter-qa/approve", { reason }).then((r) => r.data),
  rollback: (reason) => adminApi.post("/admin/letter-qa/rollback", { reason }).then((r) => r.data),
};

export const AdminEligibilityV2 = {
  flags: () => adminApi.get("/admin/eligibility-v2/flags").then((r) => r.data),
  preview: (checkId) =>
    adminApi.get(`/admin/eligibility-v2/preview/${checkId}`).then((r) => r.data),
};

// ── V2 Credit Policies (Phase 2b · CRUD + committee approval) ─────
export const AdminCreditPolicies = {
  list: (params = {}) =>
    adminApi.get("/admin/credit-policies", { params }).then((r) => r.data),
  get: (id) => adminApi.get(`/admin/credit-policies/${id}`).then((r) => r.data),
  createDraft: (body) =>
    adminApi.post("/admin/credit-policies/draft", body).then((r) => r.data),
  cloneFromActive: (product, reason) =>
    adminApi
      .post("/admin/credit-policies/clone-from-active", { product, reason })
      .then((r) => r.data),
  updateDraft: (id, patch, reason) =>
    adminApi.patch(`/admin/credit-policies/${id}`, { patch, reason }).then((r) => r.data),
  submit: (id, reason) =>
    adminApi.post(`/admin/credit-policies/${id}/submit`, { reason }).then((r) => r.data),
  approve: (id, reason, expectedHash) =>
    adminApi
      .post(`/admin/credit-policies/${id}/approve`, { reason, expected_hash: expectedHash })
      .then((r) => r.data),
  reject: (id, reason) =>
    adminApi.post(`/admin/credit-policies/${id}/reject`, { reason }).then((r) => r.data),
  returnToDraft: (id, reason) =>
    adminApi
      .post(`/admin/credit-policies/${id}/return-to-draft`, { reason })
      .then((r) => r.data),
  retire: (id, reason) =>
    adminApi.post(`/admin/credit-policies/${id}/retire`, { reason }).then((r) => r.data),
  audit: (id) => adminApi.get(`/admin/credit-policies/${id}/audit`).then((r) => r.data),
  parameterDefaults: () => adminApi.get("/admin/credit-policies/parameters/defaults").then((r) => r.data),
  parametersActive: (product) => adminApi.get("/admin/credit-policies/parameters/active", { params: { product } }).then((r) => r.data),
  simulate: (body) => adminApi.post("/admin/credit-policies/simulate", body).then((r) => r.data),
  riskGridConfig: () => adminApi.get("/admin/credit-policies/risk-grid/config").then((r) => r.data),
  riskGridSimulate: (body) => adminApi.post("/admin/credit-policies/risk-grid/simulate", body).then((r) => r.data),
  diff: (idA, idB) =>
    adminApi.get(`/admin/credit-policies/${idA}/diff/${idB}`).then((r) => r.data),
};

// ── Admin SMS operations (log, provider health, bulk retry) ─────
export const AdminSms = {
  log: (params = {}) => adminApi.get("/admin/sms/log", { params }).then((r) => r.data),
  providerHealth: () => adminApi.get("/admin/sms/provider-health").then((r) => r.data),
  egressCheck: () => adminApi.get("/admin/sms/egress-check").then((r) => r.data),
  bulkRetryPreview: () => adminApi.post("/admin/sms/bulk-retry-418/preview").then((r) => r.data),
  bulkRetryConfirm: (reason, confirmation) =>
    adminApi.post("/admin/sms/bulk-retry-418/confirm", { reason, confirmation }).then((r) => r.data),
};

// ----- Admin Partners (termination / reactivation) -----
export const AdminPartners = {
  list: (params = {}) =>
    adminApi.get("/admin/partners", { params }).then((r) => r.data),
  get: (id) => adminApi.get(`/admin/partners/${id}`).then((r) => r.data),
  terminate: (id, reason) =>
    adminApi.post(`/admin/partners/${id}/terminate`, { reason }).then((r) => r.data),
  reactivate: (id, note) =>
    adminApi.post(`/admin/partners/${id}/reactivate`, { note }).then((r) => r.data),
  resetPassword: (id, newPassword, reason = "") =>
    adminApi
      .post(`/admin/partners/${id}/reset-password`, {
        new_password: newPassword,
        reason,
      })
      .then((r) => r.data),
  audit: (id) => adminApi.get(`/admin/partners/${id}/audit`).then((r) => r.data),
};

// ----- Admin: Manual Payments review (approve/reject) -----
export const AdminManualPaymentsReview = {
  review: (paymentId, action, adminNote = "") =>
    adminApi
      .post(`/admin/manual-payments/${paymentId}/review`, {
        action,
        admin_note: adminNote,
      })
      .then((r) => r.data),
};

// ----- Manual (UPI / Bank Transfer) payments -----
export const ManualPayments = {
  coordinates: () =>
    publicApi.get("/manual-payments/coordinates").then((r) => r.data),
  submit: (fields, proofFile) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    if (proofFile) fd.append("proof", proofFile);
    return publicApi
      .post("/manual-payments/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  list: (status = "pending") =>
    adminApi.get(`/admin/manual-payments?status=${status}`).then((r) => r.data),
  review: (paymentId, payload) =>
    adminApi
      .post(`/admin/manual-payments/${paymentId}/review`, payload)
      .then((r) => r.data),
  proofUrl: (paymentId) => `${API}/manual-payments/proof/${paymentId}`,
};

// ----- Admin onboarding drip -----
export const Onboarding = {
  log: (params = {}) =>
    adminApi.get("/admin/onboarding/log", { params }).then((r) => r.data),
  runNow: () => adminApi.post("/admin/onboarding/run").then((r) => r.data),
  providerStatus: () =>
    adminApi.get("/admin/onboarding/provider-status").then((r) => r.data),
  resend: (partner_id, step_key) =>
    adminApi
      .post("/admin/onboarding/resend", { partner_id, step_key })
      .then((r) => r.data),
};

// ----- Admin bulk invitations -----
export const Invitations = {
  list: (params = {}) =>
    adminApi.get("/admin/invitations", { params }).then((r) => r.data),
  bulk: (rows) =>
    adminApi.post("/admin/invitations/bulk", { rows }).then((r) => r.data),
  uploadCsv: (file) => {
    const fd = new FormData();
    fd.append("csv_file", file);
    return adminApi
      .post("/admin/invitations/csv", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  resend: (invite_id, message) =>
    adminApi
      .post(`/admin/invitations/${invite_id}/resend`, { message: message || null })
      .then((r) => r.data),
  revoke: (invite_id) =>
    adminApi.delete(`/admin/invitations/${invite_id}`).then((r) => r.data),
  lookupPublic: (token) =>
    publicApi.get(`/invite/${token}`).then((r) => r.data),
};

// ----- AI Chat (OpenAI ChatGPT via Emergent LLM key) -----
export const PublicAiChat = {
  send: (body) => publicApi.post("/ai-chat/message/public", body).then((r) => r.data),
  history: (session_id) =>
    publicApi.get(`/ai-chat/history/${session_id}`, { params: { mode: "public" } }).then((r) => r.data),
  models: () => publicApi.get("/ai-chat/models/public").then((r) => r.data),
};

export const AdminAiChat = {
  send: (body) => adminApi.post("/ai-chat/message/admin", body).then((r) => r.data),
  history: (session_id) =>
    adminApi.get(`/ai-chat/history/${session_id}`, { params: { mode: "admin" } }).then((r) => r.data),
  models: () => adminApi.get("/ai-chat/models").then((r) => r.data),
};
